import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGaugeHigh, faAddressBook, faDiagramProject, faWallet, faBoxesStacked,
  faQrcode, faFileLines, faChevronLeft, faChevronRight,
} from '@fortawesome/free-solid-svg-icons';

// Sidebar reconstruída (fase 3 do redesign, 2026-08) — a versão anterior
// (§ fase 2) só tinha uma lista plana de itens + toggle de collapse; esta
// agrupa por área de uso (PRINCIPAL/RELACIONAMENTO/OPERAÇÃO), tem tooltip
// de verdade quando colapsada (a versão anterior dependia só do `title`
// nativo do navegador, que é lento e feio) e ganha um rodapé de conta
// (avatar + nome + Configurações), que antes não existia na sidebar — só
// um ícone solto de "sair" na topbar.
function NavItem({ id, icon, label, active, onClick, collapsed }) {
  return (
    <button
      className={`crm-nav-item${active ? ' active' : ''}`}
      onClick={() => onClick(id)}
      aria-current={active ? 'page' : undefined}
    >
      <span className="crm-nav-item-icon"><FontAwesomeIcon icon={icon} /></span>
      <span className="crm-nav-item-label">{label}</span>
      {collapsed && <span className="crm-tooltip" role="tooltip">{label}</span>}
    </button>
  );
}

function NavGroup({ label, children, collapsed }) {
  return (
    <div className="crm-nav-group">
      {!collapsed && label && <div className="crm-nav-group-label">{label}</div>}
      {children}
    </div>
  );
}

function iniciais(nome) {
  if (!nome) return '?';
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] || '') + (partes[1]?.[0] || '')).toUpperCase() || partes[0][0].toUpperCase();
}

function Sidebar({
  selecionado, onSelecionar, modulosHabilitados, permissaoClientes, crmAcessivel, empresaNome, onAbrirConfig,
  collapsed = false, onToggleCollapse, mobileOpen = false, onCloseMobile, nomeUsuario, logoEmpresa,
}) {
  const grupos = [
    {
      label: 'Principal',
      itens: [
        { id: 'inicio', label: 'Início', icon: faGaugeHigh, visivel: crmAcessivel },
      ],
    },
    {
      label: 'Relacionamento',
      itens: [
        { id: 'clientes', label: 'Clientes', icon: faAddressBook, visivel: crmAcessivel && permissaoClientes },
        { id: 'projetos', label: 'Projetos', icon: faDiagramProject, visivel: modulosHabilitados?.projetos },
      ],
    },
    {
      label: 'Operação',
      itens: [
        { id: 'financeiro', label: 'Financeiro', icon: faWallet, visivel: modulosHabilitados?.financeiro },
        { id: 'estoque', label: 'Estoque', icon: faBoxesStacked, visivel: modulosHabilitados?.estoque },
        { id: 'patrimonio', label: 'Patrimônio', icon: faQrcode, visivel: modulosHabilitados?.patrimonio },
        { id: 'documentos', label: 'Documentos', icon: faFileLines, visivel: crmAcessivel },
      ],
    },
  ]
    .map((g) => ({ ...g, itens: g.itens.filter((i) => i.visivel) }))
    .filter((g) => g.itens.length > 0);

  const selecionarEFechar = (id) => {
    onSelecionar(id);
    onCloseMobile?.();
  };

  return (
    <>
      {/* Drawer/overlay em telas de tablet (§9) — sem isso o clique fora não
          fecha a sidebar aberta por cima do conteúdo em telas estreitas. */}
      <div
        className={`crm-sidebar-backdrop${mobileOpen ? ' crm-sidebar-backdrop-open' : ''}`}
        onClick={onCloseMobile}
        aria-hidden="true"
      />
      <aside className={`crm-sidebar${collapsed ? ' crm-sidebar-collapsed' : ''}${mobileOpen ? ' crm-sidebar-mobile-open' : ''}`}>
        <div className="crm-sidebar-brand">
          {/* Logo da empresa substitui a marca padrão quando existe — mesmo
              tamanho, para a troca não deslocar o cabeçalho da sidebar. */}
          {logoEmpresa
            ? <img className="crm-sidebar-mark-img" src={logoEmpresa} alt={empresaNome || 'Logo da empresa'} />
            : <span className="crm-sidebar-mark"><img src="/lockus-icon.svg" alt="" /></span>}
          <div className="crm-sidebar-brand-text">
            <span className="crm-sidebar-brand-name">CRM</span>
            {empresaNome && <span className="crm-sidebar-brand-tenant">{empresaNome}</span>}
          </div>
        </div>

        <nav className="crm-nav">
          {grupos.map((g) => (
            <NavGroup key={g.label} label={g.label} collapsed={collapsed}>
              {g.itens.map((item) => (
                <NavItem key={item.id} id={item.id} icon={item.icon} label={item.label} active={selecionado === item.id} onClick={selecionarEFechar} collapsed={collapsed} />
              ))}
            </NavGroup>
          ))}
        </nav>

        <div className="crm-sidebar-footer">
          <button className="crm-sidebar-account" onClick={() => { onAbrirConfig(); onCloseMobile?.(); }}>
            <span className="crm-avatar crm-avatar-sm">{iniciais(nomeUsuario)}</span>
            {!collapsed && (
              <span className="crm-sidebar-account-text">
                <span className="crm-sidebar-account-name">{nomeUsuario || 'Conta'}</span>
                <span className="crm-sidebar-account-hint">Configurações</span>
              </span>
            )}
            {collapsed && <span className="crm-tooltip" role="tooltip">Configurações</span>}
          </button>
          {onToggleCollapse && (
            <button
              className="crm-icon-btn crm-sidebar-collapse-toggle"
              onClick={onToggleCollapse}
              title={collapsed ? 'Expandir menu' : 'Recolher menu'}
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              <FontAwesomeIcon icon={collapsed ? faChevronRight : faChevronLeft} />
            </button>
          )}
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
